// _shopify_init.js
// PJAX差し込み範囲に対して “安全に” Shopify/テーマの再初期化を行うユーティリティ
// 目的：重複実行やグローバル副作用を抑え、scope内だけで完結させる

const EXECUTED_SCRIPT_ATTR = 'data-executed-once'; // 重複実行ガード属性
const SKIP_SCRIPT_TYPES = new Set([
  'application/ld+json',     // 構造化データ
  'application/json',        // データスロット
  'text/template',           // テンプレ
  'text/x-handlebars-template'
]);
const _loadedExternalScripts = new Set();
export const shopifyInit = {
  /** 外部から追加できる任意の初期化フック（順序はここで管理） */
  hooks: {
	pre:  [], // DOM差し替え直後〜セクションイベント前に走らせたい
	mid:  [], // セクションイベント後、テーマ系initの前後
	post: []  // 公式/テーマ初期化の後に走らせたい
  },
  _loadExternalScriptsOnce(scope){
	// data-reinit="external" が付いた <script src> だけを対象にする（安全のため）
	const scripts = scope.querySelectorAll('script[src][data-reinit="external"]');
	scripts.forEach(orig => {
	  const src = orig.src; // 絶対URLに解決される
	  if (_loadedExternalScripts.has(src)) return; // 既に読み込み済み
  
	  const s = document.createElement('script');
	  s.src  = src;
	  // type/nomodule/defer/nonce など必要なら引き継ぐ
	  const type = orig.getAttribute('type');
	  if (type) s.type = type;
	  if (orig.noModule) s.noModule = true;
	  if (orig.defer)    s.defer = true;
	  const nonce = orig.getAttribute('nonce');
	  if (nonce) s.setAttribute('nonce', nonce);
  
	  // 一度だけロード管理
	  _loadedExternalScripts.add(src);
	  document.head.appendChild(s);
	});
  },
  /** 差し込んだDOM範囲（scope）でのみ再初期化を行う */
  reinit(scope = document) {
	try {
	  // 0) ユーザー任意のプレフック
	  this._runHooks(this.hooks.pre, scope);

	  // 1) セクションロードイベント（対象セクションへ直接投げる）
	  this._dispatchSectionEventInScope('shopify:section:load', scope);

	  // 2) テーマ初期化（存在すれば）
	  if (window.theme && typeof window.theme.init === 'function') {
		try { window.theme.init(); } catch (e) { console.warn('[shopifyInit] theme.init failed', e); }
	  }

	  // 3) 公式周辺：Shopify 支払いボタン / XR
	  try { window?.Shopify?.PaymentButton?.init(); } catch (e) { /* noop */ }
	  try { window?.ProductModel?.loadShopifyXR?.(); } catch (e) { /* noop */ }

	  // 4) ユーザー定義の“よくある初期化”
	  try {
		if (typeof window.initProductMedia     === 'function') window.initProductMedia(scope);
		if (typeof window.initVariantSelectors === 'function') window.initVariantSelectors(scope);
		if (typeof window.initSliders          === 'function') window.initSliders(scope);
		if (typeof window.initModal            === 'function') window.initModal(scope);
	  } catch (e) {
		console.warn('[shopifyInit] custom hooks failed', e);
	  }

	  // 5) ユーザー任意のミッドフック
	  this._runHooks(this.hooks.mid, scope);
	  
	  // ★ 追加：外部 <script src>（data-reinit="external" のみ）を一度だけ読み込み
	  this._loadExternalScriptsOnce(scope);
	  // 6) scope内の inline <script> を“必要なものだけ”一度だけ評価
	  this._evalInlineScriptsOnce(scope);

	  // 7) ユーザー任意のポストフック
	  this._runHooks(this.hooks.post, scope);
	  console.log("reinit shopify")
	} catch (e) {
	  console.error('[shopifyInit.reinit] failed', e);
	}
  },

  /** 差し込み範囲を破棄する前に unload を送って、イベント解除などを促す */
  unload(scope = document) {
	try {
	  this._dispatchSectionEventInScope('shopify:section:unload', scope);
	} catch (e) {
	  console.warn('[shopifyInit.unload] failed', e);
	}
  },

  /** 任意のフックを登録（'pre' | 'mid' | 'post'） */
  addHook(phase, fn) {
	if (!['pre','mid','post'].includes(phase) || typeof fn !== 'function') return;
	this.hooks[phase].push(fn);
  },

  // ---- 内部ユーティリティ ----
  _runHooks(list, scope) {
	for (const fn of list) {
	  try { fn(scope); } catch (e) { console.warn('[shopifyInit hook] error', e); }
	}
  },

  _dispatchSectionEventInScope(type, scope) {
	const sections = scope.querySelectorAll('[data-section-id]');
	sections.forEach(sec => {
	  const id = sec.getAttribute('data-section-id');
	  // section自体にイベントを投げる（バブリングtrueにしておくと上位も拾える）
	  sec.dispatchEvent(new CustomEvent(type, {
		bubbles: true,
		detail: { sectionId: id, target: sec }
	  }));
	});
  },

  _evalInlineScriptsOnce(scope) {
	const scripts = scope.querySelectorAll('script');
	
	scripts.forEach(orig => {
	  const src  = orig.getAttribute('src');
	  const type = (orig.getAttribute('type') || 'text/javascript').toLowerCase();

	  // 外部スクリプトは再ロードしない
	  if (src) return;

	  // 実行不要なタイプはスキップ
	  if (SKIP_SCRIPT_TYPES.has(type)) return;

	  // data-executed-once が付いていたらスキップ（重複実行防止）
	  if (orig.hasAttribute(EXECUTED_SCRIPT_ATTR)) return;

	  // 明示的に「再実行しない」印がある場合（例：data-no-reinit）もスキップ
	  if (orig.hasAttribute('data-no-reinit')) return;

	  // 実行（CSP対応でnonceを付けたい場合はここでコピー）
	  const s = document.createElement('script');
	  if (type === 'module') s.type = 'module';
	  // nonce引継ぎ（CSP環境用、存在すれば）
	  const nonce = orig.getAttribute('nonce');
	  if (nonce) s.setAttribute('nonce', nonce);
	  s.textContent = orig.textContent;

	  // 一度だけ実行マーク（元のscriptに付与）
	  orig.setAttribute(EXECUTED_SCRIPT_ATTR, 'true');

	  document.body.appendChild(s);
	  // 即時削除でリーク防止
	  setTimeout(() => s.remove(), 0);
	});
  }
  
};

export default shopifyInit;